import { loadOpeningAssessment, syncOpeningAssessment } from "./openingAssessmentV4";
/* ============================================================== */
/*                                                                */
/*   ScreenV4 · sequence ของหน้า "คัดกรอง" (HIS V4)                */
/*   (อาการนำ · ประวัติสุขภาพ · Vital Sign · Body Composition)     */
/*                                                                */
/*   practice เดียวกับ ExamV4 / PatientOrdersV4 / DocumentV4:       */
/*     data    -> state.ScreenV4Sequence                          */
/*     loading -> state.buttonLoadCheck["CardScreenV4_<ACTION>"]  */
/*     error   -> state.errorMessage["CardScreenV4"]              */
/*     card เรียก props.runSequence({ sequence, action, card, ... })*/
/*                                                                */
/* ============================================================== */

import TriageLatest from "issara-sdk/apis/TriageLatest_core";
import TriageList from "issara-sdk/apis/TriageList_core";
import TriageDetail from "issara-sdk/apis/TriageDetail_core";
import { asText } from "../../patient/examModelV4";
import ProblemListList from "issara-sdk/apis/ProblemListList_apps_DPO";
import ProblemListDetail from "issara-sdk/apis/ProblemListDetail_apps_DPO";
import AdverseReactionList from "issara-sdk/apis/AdverseReactionList_apps_ADR";
import AdverseReactionDetail from "issara-sdk/apis/AdverseReactionDetail_apps_ADR";
import VitalSignTypeList from "issara-sdk/apis/VitalSignTypeList_apps_PTM";
import MeasurementList from "issara-sdk/apis/MeasurementList_apps_PTM";
import ClinicalTermList from "issara-sdk/apis/ClinicalTermList_core";
import MultiAdverseReactionList from "issara-sdk/apis/MultiAdverseReactionList_apps_ADR";
import MeasurementCancelView from "issara-sdk/apis/MeasurementCancelView_apps_PTM";
import EncounterActionOnly from "issara-sdk/apis/EncounterActionOnly_core";
import EncounterDetail from "issara-sdk/apis/EncounterDetail_core";
import BodyCompositionList from "issara-sdk/apis/BodyCompositionList_apps_PTM";
import BodyCompositionDetail from "issara-sdk/apis/BodyCompositionDetail_apps_PTM";
import BodyCompositionOCRView from "issara-sdk/apis/BodyCompositionOCRView_apps_PTM";

import {
  BLC_STATUS,
  generatePrefixedActions,
} from "react-lib/apps/HISV3/common/CommonInterface";

import {
  apiErrorTextOf,
  errorTextOf,
  markButtonLoadCheck,
  seqBtnKey,
  seqErrorKey,
  writeSequenceState,
  type BLC_STATUS_TYPE,
  type SeqMeta,
} from "./sequenceStateV4";

import {
  asNumber,
  beDateText,
  beDateTimeText,
  nowBeDateTimeText,
  clinicalTermOptionsFromApi,
  ENCOUNTER_STATUS_NEEDS_SCREEN_ACTION,
  defaultClinicalTermId,
  friendlyTriageErrorText,
  triageBlockedMessageOf,
  triageEligibilityOf,
  hourMinuteText,
  isEncounterAlreadyQueued,
  itemsOf,
  measurementCreatePayload,
  nowHourMinuteText,
  hasProfileValue,
  friendlyBodyCompositionErrorText,
  bodyCompositionCreatePayload,
  bodyCompositionFromApi,
  bodyCompositionOcrFromApi,
  bodyCompositionSaveGateErrorOf,
  bodyCompositionSavedMessageOf,
  problemPlanOf,
  profileAuditOf,
  splitProfileProblems,
  todayBeDateText,
  type ProblemListRow,
  vitalSignMeasurementsFromApi,
  vitalSignTypesFromApi,
  INITIAL_SCREEN_SLICE,
  EMPTY_TRIAGE,
  EMPTY_HEALTH_PROFILE,
  STANDARD_VITAL_SIGN_TYPES,
  type ClinicalTermOption,
  type AllergyItem,
  type BodyCompositionRecord,
  type BodyCompositionOcrResult,
  type HealthProfileData,
  type NewVitalSignInput,
  type ScreenRunParams,
  type ScreenV4Slice,
  type TriageData,
  type VitalAuditEntry,
  type VitalSignMeasurement,
  type VitalSignType,
} from "../../patient/screenModelV4";

/* ============================================================== */
/*   SECTION 1 · TYPE-DEFINING CONSTANTS                           */
/* ============================================================== */

export const SEQUENCE_NAME = "ScreenV4" as const;
export const CARD_NAME = "CardScreenV4";

export const ACTIONS = {
  LOAD: "LOAD",
  SAVE_TRIAGE: "SAVE_TRIAGE",
  SAVE_PROFILE: "SAVE_PROFILE",
  SAVE_VITAL_SIGN: "SAVE_VITAL_SIGN",
  DELETE_MEASUREMENT: "DELETE_MEASUREMENT",
  SAVE_BODY_COMPOSITION: "SAVE_BODY_COMPOSITION",
  OCR_BODY_COMPOSITION: "OCR_BODY_COMPOSITION",
  SEND_TO_QUEUE: "SEND_TO_QUEUE",
} as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const SCREEN_SLICE_KEY = "ScreenV4Sequence";

export type State = {
  ScreenV4Sequence: Partial<ScreenV4Slice> | null;
};

export const StateInitial: State = { ScreenV4Sequence: null };

type Controller = {
  getState: () => Record<string, any>;
  setState: (state: any, callback?: () => void) => any;
  apiToken?: string;
  data?: Record<string, any>;
  dispatch?: (action: any) => void;
  handleEvent?: (event: { message: string; params?: any }) => any;
};

type Handler = (controller: Controller, params: ScreenRunParams) => Promise<any> | any;

/* ============================================================== */
/*   SECTION 2 · SLICE HELPERS                                     */
/* ============================================================== */

export const currentScreenSlice = (controller: Controller): ScreenV4Slice => {
  const raw = controller.getState()?.[SCREEN_SLICE_KEY];
  if (!raw) return { ...INITIAL_SCREEN_SLICE, sequenceIndex: "Action" };
  return {
    triage: raw.triage || EMPTY_TRIAGE,
    healthProfile: raw.healthProfile || EMPTY_HEALTH_PROFILE,
    problemRows: raw.problemRows,
    problemRowsUpdatedAt: raw.problemRowsUpdatedAt,
    panelUpdatedAt: raw.panelUpdatedAt,
    /* master ต้องเป็นของหน่วยงานจริงเท่านั้น (ห้ามเติมค่าปลอมให้ UI) */
    vitalSignTypes: raw.vitalSignTypes || [],
    vitalSignMeasurements: raw.vitalSignMeasurements || [],
    bodyCompositionList: raw.bodyCompositionList || [],
    bodyCompositionOcr: raw.bodyCompositionOcr ?? null,
    bodyCompositionSavedMessage: raw.bodyCompositionSavedMessage || "",
    latestHeight: raw.latestHeight,
    sentToQueue: Boolean(raw.sentToQueue),
    sentEncounterStatus: raw.sentEncounterStatus,
    triageCaseOptions: raw.triageCaseOptions || [],
    arriveStatusOptions: raw.arriveStatusOptions || [],
    savedAt: raw.savedAt,
    savedBy: raw.savedBy,
    vitalSavedAt: raw.vitalSavedAt,
    vitalAudit: raw.vitalAudit || [],
    encounterStatus: raw.encounterStatus,
    encounterStatusName: raw.encounterStatusName,
    triageEligibility: raw.triageEligibility,
    triageNotice: raw.triageNotice,
    assessmentWarning: raw.assessmentWarning,
    partialErrors: raw.partialErrors || [],
    patientHn: raw.patientHn,
    encounterId: raw.encounterId,
    patientId: raw.patientId,
    doctorId: raw.doctorId,
    sequenceIndex: "Action",
  };
};

const writeSlice = (
  controller: Controller,
  meta: SeqMeta,
  sliceUpdate: Partial<ScreenV4Slice>,
  loadingStatus?: BLC_STATUS_TYPE,
  error?: string | null,
) => {
  const current = currentScreenSlice(controller);
  const nextSlice: ScreenV4Slice = {
    ...current,
    ...sliceUpdate,
    sequenceIndex: "Action",
  };

  const patch: Record<string, any> = {
    [SCREEN_SLICE_KEY]: nextSlice,
  };

  if (loadingStatus !== undefined) {
    const btnKey = seqBtnKey(meta);
    const existing = controller.getState()?.buttonLoadCheck || {};
    patch.buttonLoadCheck = { ...existing, [btnKey]: loadingStatus };
  }

  if (error !== undefined) {
    const errKey = seqErrorKey(meta);
    const existing = controller.getState()?.errorMessage || {};
    patch.errorMessage = { ...existing, [errKey]: error };
  }

  controller.setState(patch);
};

/** ส่วนหัวของหน้า (issue 86613 ข้อ 3): ทุกบันทึกต้อง-update "บันทึกล่าสุด ... โดย ..." */
const savedStampOf = (controller: Controller, params: ScreenRunParams): { savedAt: string; savedBy: string; panelUpdatedAt: string } => {
  const state = controller.getState();
  const name =
    asText(params.savedBy) ||
    asText(state.userProfile?.name) ||
    asText(state.userProfile?.username) ||
    asText(state.django?.user?.username) ||
    "ผู้บันทึก";
  return { savedAt: nowBeDateTimeText(), savedBy: name, panelUpdatedAt: new Date().toISOString() };
};

/* ============================================================== */
/*   SECTION 3 · ACTION HANDLERS                                   */
/* ============================================================== */

/**
 * 1. LOAD: ดึงข้อมูลตั้งต้น (Triage, Health Profile, Vital Sign, Body Composition)
 */
export const LoadScreen: Handler = async (controller, params) => {
  const meta: SeqMeta = { sequence: SEQUENCE_NAME, card: CARD_NAME, action: ACTIONS.LOAD };
  writeSlice(controller, meta, {}, BLC_STATUS.LOADING, null);

  const state = controller.getState();
  const encounterId = asNumber(params.encounterId) ?? asNumber(state.selectedEncounter?.id);
  const patientId = asNumber(params.patientId) ?? asNumber(state.selectedEncounter?.patient_id);
  const divisionId = asNumber(controller.data?.division) ?? asNumber(state.selectedDivision?.id);

  if (!encounterId) {
    // ไม่มี encounterId -> ใช้ Mock Data สำหรับ preview mode
    writeSlice(
      controller,
      meta,
      {
        ...INITIAL_SCREEN_SLICE,
        encounterId,
        patientId,
      },
      BLC_STATUS.SUCCESS,
      null,
    );
    return;
  }

  try {
    const [
      triageRes, problemRes, adrRes, vitalTypeRes, measureRes, caseRes, arriveRes, encounterRes, bodyCompRes,
    ] = await Promise.all([
      TriageLatest.retrieve({
        params: { encounter: encounterId },
        apiToken: controller.apiToken,
      }).catch(() => [null]),
      patientId
        ? ProblemListList.list({
            params: { patient: patientId, is_active: true },
            apiToken: controller.apiToken,
          }).catch(() => [null])
        : Promise.resolve([null]),
      patientId
        ? AdverseReactionList.list({
            params: { patient: patientId },
            apiToken: controller.apiToken,
          }).catch(() => [null])
        : Promise.resolve([null]),
      /* master ของชนิดสัญญาณชีพต้องเป็นของหน่วยงานของ encounter นี้เท่านั้น
         (backend check_allow_update: VitalSignType.divisions ต้องมี request.division
          และ VitalSignTypeList มีพารามิเตอร์ div_from_encounter) */
      VitalSignTypeList.list({
        params: { encounter: encounterId, div_from_encounter: true },
        apiToken: controller.apiToken,
        extra: { division: divisionId },
      }).catch(() => [null]),
      MeasurementList.list({
        params: {
          encounter: encounterId,
          sort_by_seq: true,
          use_patient: true,
          invert_order: true,
        },
        apiToken: controller.apiToken,
      }).catch(() => [null]),
      ClinicalTermList.list({
        params: { type: "PC" },
        apiToken: controller.apiToken,
      }).catch(() => [null]),
      ClinicalTermList.list({
        params: { type: "PAB" },
        apiToken: controller.apiToken,
      }).catch(() => [null]),
      /* issue 86620 ข้อ 4/8: ต้องรู้ว่า Encounter อยู่สถานะไหนจริง
         (ยังไม่ส่ง = WAIT_TRIAGE/ARRIVING/ARRIVED/SCREENED, ส่งแล้ว = IN_QUEUE เป็นต้นไป) */
      EncounterDetail.retrieve({
        pk: encounterId,
        apiToken: controller.apiToken,
        extra: { division: divisionId },
      }).catch(() => [null]),
      /* issue 86758: ประวัติ Body Composition เก็บใน PTM.BodyComposition (ย้อนหลังตาม HN ได้) */
      BodyCompositionList.list({
        params: { encounter: encounterId, use_patient: true, invert_order: true },
        apiToken: controller.apiToken,
        extra: { division: divisionId },
      }).catch(() => [null]),
    ]);

    // Parse Triage
    const rawTriage = triageRes?.[0];
    const triageEligibility = triageEligibilityOf(
      (encounterRes as any)?.[0],
      Boolean(rawTriage && rawTriage.id),
      encounterId,
    );
    const triage: TriageData = rawTriage
      ? {
          id: rawTriage.id,
          chiefComplaint: rawTriage.chief_complaint || "",
          triageLevel: rawTriage.triage_level ? String(rawTriage.triage_level) : undefined,
          arriveStatus: rawTriage.arrive_status,
          arriveStatusLabel: rawTriage.arrive_status_label || "",
          arriveStatusId: asNumber(rawTriage.arrive_status),
          caseId: asNumber(rawTriage.case),
        }
      : { ...EMPTY_TRIAGE };

    const enc = state.selectedEncounter;

    /* Parse Health Profile (issue 86615: ProblemList เป็นแหล่งเก็บตาม HN ของ
       โรคประจำตัว / ยาที่ใช้ประจำ / ประวัติผ่าตัด - อ่านย้อนหลังได้ทุ encounter) */
    const rawProblems = itemsOf(problemRes?.[0]);
    const problemRows: ProblemListRow[] = rawProblems
      .map((row: any) => ({
        id: asNumber(row.id) ?? 0,
        message: String(row.message ?? row.name ?? ""),
        is_active: row.is_active !== false,
        edited: row.edited,
        created: row.created,
        edit_user: row.edit_user,
        edit_user_name: row.edit_user_name,
      }))
      .filter((row: ProblemListRow) => row.id > 0);
    const problems = splitProfileProblems(problemRows);

    const rawAdrs = itemsOf(adrRes?.[0]);
    /* แถวที่ถูก CANCEL หรือถูกปิดไว้ด้วย is_inactive (วิธี "เอา NKDA ออก" ของ backend)
       ไม่ใช่ข้อมูลปัจจุบันบนหน้า ADR ทั้งนั้น */
    const liveAdrRow = (a: any): boolean =>
      !!a && a.status !== "CANCELED" && a.action !== "CANCEL" && a.is_inactive !== true;
    /* แถวแพ้ที่ชื่อ "ไม่มี"/"ไม่ระบุ" (ของเก่าที่ V3 ให้ผู้ใช้พิมพ์ลงช่องชื่อสาร) ไม่ถือว่าเป็นประวัติแพ้
       เช่น AdverseReaction id 4519 ของผู้ป่วย 3442: name "ไม่มี" status NOTED */
    const realAllergyNameRow = (a: any): boolean =>
      hasProfileValue(a?.name || a?.adr_short_info || a?.note);
    const isNkdaRow = (a: any): boolean =>
      a?.note?.includes("ปฏิเสธการแพ้") || a?.action === "NO_REACTION" || a?.status === "NO_REACTION";
    const nkdaRow = rawAdrs.find((a: any) => liveAdrRow(a) && isNkdaRow(a));
    const nkdaRecordId = asNumber((nkdaRow || {}).id);
    let allergies: AllergyItem[] = rawAdrs
      .filter((a: any) =>
        liveAdrRow(a) &&
        !isNkdaRow(a) &&
        realAllergyNameRow(a)
      )
      .map((a: any) => {
        const typeStr: AllergyItem["type"] =
          a.type === 1 || a.type_name === "ยา" || a.type_name_name === "DRUG"
            ? "ยา"
            : a.type === 2 || a.type_name === "อาหาร" || a.type_name_name === "FOOD"
              ? "อาหาร"
              : a.type === 3 || a.type_name === "เวชภัณฑ์" || a.type_name_name === "SUPPLIES"
                ? "สารเคมี"
                : "อื่น ๆ";
        return {
          id: a.id,
          type: typeStr,
          name: a.name || a.adr_short_info || a.note || "ไม่ระบุชื่อ",
          react: a.other_symptom || a.symptoms_name || a.note || "-",
        };
      });

    /* ปุ่ม NKDA ต้องสอดคล้องกับข้อมูลจริง: มีแถว status NO_REACTION ที่ยังไม่ถูกปิด
       และต้องไม่มีประวัติแพ้จริง (แบบเดียวกับ V3 CardSubAllergyHistory:100 items.every(NO_REACTION)) */
    let isNkda = Boolean(nkdaRow) && allergies.length === 0;

    if (allergies.length === 0 && !isNkda) {
      if (enc?.drug_allergy) {
        if (enc.drug_allergy.includes("ปฏิเสธ")) {
          isNkda = true;
        } else {
          allergies.push({ type: "ยา", name: enc.drug_allergy, react: "-" });
        }
      }
      if (enc?.other_allergy) {
        if (enc.other_allergy.includes("ปฏิเสธ")) {
          isNkda = true;
        } else {
          allergies.push({ type: "อาหาร", name: enc.other_allergy, react: "-" });
        }
      }
      /* เดิมมี fallback "ไม่มีแถวแพ้ = NKDA" ซึ่งเป็นการเดาที่ทำให้ปุ่มยังติ๊กอยู่ทั้งที่ผู้ใช้
         ถอดติ๊ก/ยกเลิกแถวจริงไปแล้ว -> ถือว่ายังไม่บันทึก จนกว่าจะกดปุ่มเอง (สร้างแถว NO_REACTION) */
    }

    /* ยาที่ใช้ประจำ: ProblemList (ที่บันทึกจากหน้าคัดกรอง) ก่อน แล้วค่อย fallback ไปของ encounter */
    const encounterMedications =
      Array.isArray(enc?.regularly_taken_supplement_data_list) && enc.regularly_taken_supplement_data_list.length > 0
        ? enc.regularly_taken_supplement_data_list.map((m: any) => m.name).join(", ")
        : "";

    const audit = profileAuditOf(problemRows, rawAdrs, String(state.userName || ""));

    const healthProfile: HealthProfileData = {
      underlyingDiseases: problems.underlyingDiseases || enc?.underlying_disease || "ไม่มี",
      surgicalHistory: problems.surgicalHistory || "ไม่มี",
      currentMedications: problems.currentMedications || encounterMedications || "ไม่มี",
      smoking: problems.smoking || "ไม่สูบ",
      alcohol: problems.alcohol || "ไม่ดื่ม",
      nkda: isNkda,
      allergies: allergies.length > 0 ? allergies : [],
      nkdaRecordId,
      recordedBy: audit?.recordedBy,
      recordedAt: audit?.recordedAt,
      firstSaved: audit?.firstSaved,
    };

    // Parse Vital Sign Types
    /* ห้าม fallback เป็น master ปลอม hardcoded - ถ้าหน่วยงานนี้ยังไม่กำหนดชนิดสัญญาณชีพ
       การบันทึกจะถูก backend ตอบ 400 "หน่วยงาน ... ไม่สามารถบันทึก ... ได้" ทุกครั้ง */
    const vitalSignTypes: VitalSignType[] = vitalSignTypesFromApi(vitalTypeRes?.[0]);

    // Parse Measurements (GET /apis/PTM/measurement/ -> {items:[{id,date,time,edit_user_name,vital_signs:[...]}]})
    const vitalSignMeasurements: VitalSignMeasurement[] = vitalSignMeasurementsFromApi(measureRes?.[0]);

    const triageCaseOptions: ClinicalTermOption[] = clinicalTermOptionsFromApi(caseRes?.[0]);
    const arriveStatusOptions: ClinicalTermOption[] = clinicalTermOptionsFromApi(arriveRes?.[0]);
    let assessmentWarning = "";
    if (!rawTriage?.id) {
      try {
        const form = await loadOpeningAssessment(encounterId, { apiToken: controller.apiToken, extra: { division: divisionId } });
        if (form) {
          const arrive = arriveStatusOptions.find((item) => item.code === form.data?.arrive_status);
          triage.chiefComplaint = String(form.data?.chief_complaint ?? "");
          triage.arriveStatusId = arrive?.id;
          triage.arriveStatus = arrive?.id;
          triage.arriveStatusLabel = arrive?.name;
        }
      } catch (error) { assessmentWarning = `อ่านข้อมูลตอนเปิด Encounter ไม่สำเร็จ: ${errorTextOf(error)}`; }
    }

    /* Body Composition (issue 86619 + 86758): แถวที่อ่านกลับจาก API ต้องมี id จริงเท่านั้น */
    const bodyCompositionList: BodyCompositionRecord[] = itemsOf(bodyCompRes?.[0])
      .map((row: any) => bodyCompositionFromApi(row))
      .filter((record): record is BodyCompositionRecord => !!record && record.id !== undefined);

    const partialErrors = [
      triageRes?.[0] ? null : "triage",
      problemRes?.[0] ? null : "problems",
      adrRes?.[0] ? null : "allergies",
      vitalTypeRes?.[0] ? null : "vitalSignTypes",
      measureRes?.[0] ? null : "vitalSignMeasurements",
      bodyCompRes?.[0] ? null : "bodyComposition",
    ].filter((key): key is string => typeof key === "string");

    const latestWithHeight = vitalSignMeasurements.find((m) => m.values?.["HEIGHT"] !== undefined);
    const latestHeight = latestWithHeight ? asNumber(latestWithHeight.values["HEIGHT"]) : undefined;

    writeSlice(
      controller,
      meta,
      {
        triage,
        healthProfile,
        ...(problemRes?.[0] ? { problemRows, problemRowsUpdatedAt: new Date().toISOString() } : {}),
        vitalSignTypes,
        vitalSignMeasurements,
        triageCaseOptions,
        arriveStatusOptions,
        savedAt: beDateTimeText(rawTriage?.edited),
        savedBy: rawTriage?.edit_user_fullname || undefined,
        partialErrors,
        assessmentWarning,
        bodyCompositionList,
        bodyCompositionOcr: null,
        bodyCompositionSavedMessage: "",
        latestHeight,
        encounterId,
        patientId,
        encounterStatus: asNumber((encounterRes as any)?.[0]?.status),
        encounterStatusName: String((encounterRes as any)?.[0]?.status_name ?? ""),
        /* โหลดใหม่ต้องไม่จำเฉพาะใน memory: ถ้า backend ส่งเข้าคิวแล้ว ต้องขึ้น "ส่งเข้าคิวแล้ว" และ
           ปุ่มส่งปิดไว้ แต่ยังคงแก้ข้อมูล/บันทึก Vital Sign เพิ่มได้ (issue 86620 ข้อ 8) */
        /* pre-check ก่อนสร้างการคัดกรอง (issue 86747): ดู allowed_actions ของ Encounter จริง
           ถ้า encounter นี้มี triage เดิมอยู่แล้ว บันทึกได้ผ่าน PATCH ไม่ติด validate_patient_id */
        triageEligibility,
        triageNotice: triageEligibility.known && !triageEligibility.allowsCreate
          ? triageBlockedMessageOf(triageEligibility)
          : undefined,
        sentToQueue: isEncounterAlreadyQueued(asNumber((encounterRes as any)?.[0]?.status)),
        sentEncounterStatus: String((encounterRes as any)?.[0]?.status_name ?? "") || undefined,
      },
      BLC_STATUS.SUCCESS,
      null,
    );
  } catch (err: any) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, errorTextOf(err));
  }
};

/**
 * 2. SAVE_TRIAGE: บันทึกอาการนำและระดับคัดกรอง
 */
export const SaveTriage: Handler = async (controller, params) => {
  const meta: SeqMeta = { sequence: SEQUENCE_NAME, card: CARD_NAME, action: ACTIONS.SAVE_TRIAGE };
  writeSlice(controller, meta, {}, BLC_STATUS.LOADING, null);

  const slice = currentScreenSlice(controller);
  const triageData = (params.triage as TriageData) || slice.triage;
  const encounterId = asNumber(params.encounterId) ?? slice.encounterId;

  /* issue 86614: "อาการนำ (Chief complaint) *" บังคับกรอก - ช่องว่างอย่างเดียวถือวาไมไดกรอก */
  const chiefComplaint = String(triageData?.chiefComplaint ?? "").trim();
  if (!chiefComplaint) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, "กรุณาระบุอาการนำ (Chief complaint) กอนบันทึกการคัดกรอง");
    return;
  }
  triageData.chiefComplaint = chiefComplaint;
  const patientId =
    asNumber(params.patientId) ?? slice.patientId ?? asNumber(controller.getState().selectedEncounter?.patient_id);
  const divisionId = asNumber(controller.data?.division) ?? asNumber(controller.getState().selectedDivision?.id);

  try {
    let savedId = triageData.id || slice.triage?.id;

    if (savedId) {
      // Update: PATCH /apis/core/triage/<id>/ (TriageSerializerForUpdate, fields = '__all__')
      const [resp, err] = await TriageDetail.patch({
        pk: savedId,
        data: {
          chief_complaint: chiefComplaint,
          triage_level: triageData.triageLevel,
          division: divisionId,
        } as any,
        apiToken: controller.apiToken,
      });
      if (err) throw err;
      if (resp?.id) savedId = resp.id;
    } else if (encounterId) {
      /* Create: Triage.case + Triage.arrive_status เป็น ClinicalTerm FK ที่ backend บังคับ
         ห้าม hardcode id (ค่า 54/62 บน staging คือ is_default ของ type "PC"/"PAB") -> อ่านจาก master */
      const caseId =
        triageData.caseId ??
        slice.triage?.caseId ??
        defaultClinicalTermId(slice.triageCaseOptions);
      const arriveStatusId =
        triageData.arriveStatusId ??
        asNumber(triageData.arriveStatus) ??
        asNumber(slice.triage?.arriveStatus) ??
        defaultClinicalTermId(slice.arriveStatusOptions);

      if (!caseId || !arriveStatusId) {
        writeSlice(
          controller,
          meta,
          {},
          BLC_STATUS.ERROR,
          "ยังไม่ master ของ triage (ClinicalTerm type PC/PAB) - โหลดหน้าใหม่ก่อนบันทึกอาการนำ",
        );
        return;
      }

      /* backend (his/core/serializers.py::TriageSerializer - POST /apis/core/triage/) ผูก triage กับ
         "encounter ที่รอคัดกรอง (WAIT_TRIAGE) ของผู้ป่วย" ผ่าน **patient_id** ไม่ใช่ encounter ที่กำลังเปิดอยู่
         - patient_id เป็น write_only PrimaryKeyRelatedField ที่ backend บังคับ (ขาด -> 400 "ฟิลด์นี้จำเป็น")
         - Triage model ไม่มี field encounter (Encounter ต่างหากที่เป็น FK ไป triage) -> ห้ามส่ง encounter
         - referer เป็น CharField(null=False, blank=True) ต้องส่ง (ว่างได้) */
      /* pre-check (issue 86747): ถ้า Encounter ที่เปิดอยู่ไม่อนุญาต OPD_TRIAGE ตาม allowed_actions ของ backend
         การยิง POST จะล้มเหลวเสมอ (validate_patient_id มองหา encounter WAIT_TRIAGE ของผู้ป่วย)
         -> อธิบายผู้ใช้ก่อน ไม่ต้องยิง API */
      const eligibility = slice.triageEligibility;
      if (eligibility && eligibility.known && !eligibility.allowsCreate) {
        writeSlice(controller, meta, {}, BLC_STATUS.ERROR, triageBlockedMessageOf(eligibility));
        return;
      }

      if (!patientId) {
        writeSlice(
          controller,
          meta,
          {},
          BLC_STATUS.ERROR,
          "ไม่พบ patient_id ของผู้ป่วย - บันทึกการคัดกรองไม่ได้ (backend ต้องใช้ patient_id)",
        );
        return;
      }

      const [resp, err] = await TriageList.create({
        data: {
          patient_id: patientId,
          chief_complaint: chiefComplaint,
          triage_level: triageData.triageLevel,
          division: divisionId,
          arrive_status: arriveStatusId,
          case: caseId,
          patient_type: triageData.patientType || "WLK",
          referer: String(triageData.referer ?? ""),
        } as any,
        apiToken: controller.apiToken,
        extra: { division: divisionId },
      });
      if (err) throw err;
      if (resp?.id) savedId = resp.id;
    }

    /* อ่านแถว "ตัวจริง" ของ Encounter กลับมาจาก backend เสมอ
       ตรวจกับ API จริง (mehealth-prod-2):
       - POST /apis/core/triage/ ตอบ id 2907 แต่ encounter 9838 ถูกผูกกับ 2908 (และ 9841 ผูก 2909)
         เพราะ TriageSerializer.update_encounters() deepcopy Triage ต่อ 1 encounter
       - PATCH /apis/core/triage/2908/ แล้ว encounter 9838 กลับผูกกับ 2910 (TriageSerializerForUpdate.update
         เรียก update_encounters ด้วย) -> id ที่เราเก็บไว้กลายเป็นแถวกำพร้า
     ถ้าใช้ id จาก response โดยไม่อ่านซ้ำ การกดบันทึกครั้งถัดไปจะ PATCH แถวกำพร้า และข้อมูลที่แก้จะไม่แสดง
     หลังโหลดหน้าใหม่ (ตรงกับที่ tester เจอ: ปิดแล้วเปิด tab ใหม่ไม่เห็นอาการนำ) */
    let canonicalTriage: any = null;
    if (encounterId) {
      const recheck = await TriageLatest
        .retrieve({
          params: { encounter: encounterId },
          apiToken: controller.apiToken,
          extra: { division: divisionId },
        })
        .catch(() => [null]);
      canonicalTriage = (recheck as any)?.[0] || null;
      if (canonicalTriage?.id) savedId = canonicalTriage.id;
    }

    const updatedTriage: TriageData = {
      ...slice.triage,
      ...triageData,
      id: savedId || triageData.id || slice.triage?.id,
    };

    if (canonicalTriage) {
      updatedTriage.arriveStatus = canonicalTriage.arrive_status ?? updatedTriage.arriveStatus;
      updatedTriage.arriveStatusId = asNumber(canonicalTriage.arrive_status) ?? updatedTriage.arriveStatusId;
      updatedTriage.arriveStatusLabel = canonicalTriage.arrive_status_label || updatedTriage.arriveStatusLabel;
      updatedTriage.caseId = asNumber(canonicalTriage.case) ?? updatedTriage.caseId;
      updatedTriage.patientType = canonicalTriage.patient_type || updatedTriage.patientType;
      updatedTriage.referer =
        canonicalTriage.referer !== undefined ? canonicalTriage.referer : updatedTriage.referer;
    }

    /* triage ถูกบันทึก + อ่าน id ของแถวจริงกลับมาแล้ว -> รายงานสำเร็จทันที
       เดิมปุ่มยังหมุนต่อจนจบซิงก์แบบประเมินแรกรับ (form-data-latest 2 ครั้ง + PUT)
       ผู้ใช้จึงเห็นว่า "กำลังบันทึก" ทั้งที่ api ของ triage ตอบไปแล้ว และถ้าฝั่ง form-data ช้า/พัง
       ก็หมุนอยู่เป็นนาที  -> แยกเป็นคำเตือนใต้หัวข้อแทน */
    writeSlice(
      controller,
      meta,
      { triage: updatedTriage, ...savedStampOf(controller, params) },
      BLC_STATUS.SUCCESS,
      null,
    );

    let assessmentWarning = "";
    if (encounterId) {
      try {
        const arriveId = updatedTriage.arriveStatusId ?? asNumber(updatedTriage.arriveStatus);
        const arriveCode = slice.arriveStatusOptions?.find((item) => item.id === arriveId)?.code;
        await syncOpeningAssessment(encounterId, {
          arrive_status: arriveCode, chief_complaint: chiefComplaint, triage_level: updatedTriage.triageLevel ?? "",
        }, { apiToken: controller.apiToken, extra: { division: divisionId } });
      } catch (error) { assessmentWarning = `บันทึกคัดกรองแล้ว แต่ซิงก์แบบประเมินแรกรับไม่สำเร็จ: ${errorTextOf(error)}`; }
    }
    // แจ้ง refresh encounter ให้ panel อัปเดต
    controller.handleEvent?.({
      message: "HandleRefreshEncounter",
      params: {},
    });

    writeSlice(
      controller,
      meta,
      { triage: updatedTriage, assessmentWarning, ...savedStampOf(controller, params) },
      BLC_STATUS.SUCCESS,
      null,
    );
    return updatedTriage;
  } catch (err: any) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, friendlyTriageErrorText(errorTextOf(err)));
  }
};

/**
 * 3. SAVE_PROFILE: บันทึกประวัติสุขภาพประจำตัว (โรคประจำตัว, ประวัติแพ้, ยาเดิม)
 */
export const SaveProfile: Handler = async (controller, params) => {
  const meta: SeqMeta = { sequence: SEQUENCE_NAME, card: CARD_NAME, action: ACTIONS.SAVE_PROFILE };
  writeSlice(controller, meta, {}, BLC_STATUS.LOADING, null);

  const state = controller.getState();
  const slice = currentScreenSlice(controller);
  const profile = (params.healthProfile as HealthProfileData) || slice.healthProfile;
  const encounterId = asNumber(params.encounterId) ?? asNumber(slice.encounterId) ?? asNumber(state.selectedEncounter?.id);
  const patientId = asNumber(params.patientId) ?? asNumber(slice.patientId) ?? asNumber(state.selectedEncounter?.patient_id);

  const divisionId = asNumber(controller.data?.division) ?? asNumber(state.selectedDivision?.id);
  const partialErrors: string[] = [];
  /* id ของแถว AdverseReaction "ปฏิเสธการแพ้" (NO_REACTION) ที่อ่านมาจาก slice ล่าสุด */
  let nkdaRowId: number | undefined = asNumber(slice.healthProfile?.nkdaRecordId);

  try {
    // 1. อัปเดตข้อมูลลง Encounter (EncounterSerializer fields='__all__' -> 3 field หนนี้ writable)
    if (encounterId) {
      let drugAllergyStr: string | null = null;
      let otherAllergyStr: string | null = null;
      if (profile.nkda) {
        drugAllergyStr = "ปฏิเสธการแพ้ยา";
        otherAllergyStr = "ปฏิเสธการแพ้อาหาร/อื่น ๆ";
      } else if (Array.isArray(profile.allergies) && profile.allergies.length > 0) {
        drugAllergyStr =
          profile.allergies
            .filter((a) => a.type === "ยา")
            .map((a) => (a.react && a.react !== "-" ? `${a.name} (${a.react})` : a.name))
            .join(", ") || null;
        otherAllergyStr =
          profile.allergies
            .filter((a) => a.type !== "ยา")
            .map((a) => (a.react && a.react !== "-" ? `${a.name} (${a.react})` : a.name))
            .join(", ") || null;
      }

      const [, encErr] = await EncounterDetail.patch({
        pk: encounterId,
        data: {
          action: "OPD_EDIT",
          underlying_disease: profile.underlyingDiseases || "ไม่มี",
          drug_allergy: drugAllergyStr,
          other_allergy: otherAllergyStr,
        } as any,
        apiToken: controller.apiToken,
        extra: { division: divisionId },
      }).catch((e: any) => [null, e]);

      // ห้ามเงียบ error แล้วรายงาน SUCCESS
      if (encErr) partialErrors.push(apiErrorTextOf(encErr) || "บันทึกประวัติสุขภาพลง Encounter ไม่สำเร็จ");
    }

    if (patientId) {
      /* AdverseReaction.TYPE: DRUG=1 FOOD=2 SUPPLIES=3 OTHER=4 */
      const typeMap: Record<string, number> = { "ยา": 1, "อาหาร": 2, "สารเคมี": 3, "เวชภัณฑ์": 3, "อื่น ๆ": 4 };
      /* AdverseReactionAdversary.adversary_type: master ของหมวดอาหารใช้ "FOOD"
         ส่วนชื่อที่พิมพ์เอง (ยา/สารเคมี/อื่น ๆ) ใช้ "OTHER" เหมือน V3 paramsByTypes.freetext */
      const adversaryTypeMap: Record<string, "FOOD" | "OTHER"> = {
        "ยา": "OTHER",
        "อาหาร": "FOOD",
        "สารเคมี": "OTHER",
        "เวชภัณฑ์": "OTHER",
        "อื่น ๆ": "OTHER",
      };

      /* 2a. ถอดติ๊ก "ปฏิเสธการแพ้ทุกประเภท" -> ต้องปิดแถว NO_REACTION เดิม
         AdverseReaction.TRANSITION ไม่มี (NO_REACTION -> CANCEL) -> backend ตอบ 400 ACTION_NOT_ALLOWED
         วิธีที่ใช้ได้จริงคือ keeping status เดิมแล้วตั้ง is_inactive + inactive_start_date
         (AdverseReactionList กรองแถว is_inactive ที่วัน <= วันนี้ ออกจาก GET ค่าเริ่มต้น) */
      if (!profile.nkda && nkdaRowId) {
        const [, nkdaErr] = await AdverseReactionDetail.patch({
          pk: nkdaRowId,
          data: {
            action: "NO_REACTION",
            is_inactive: true,
            inactive_start_date: new Date().toISOString().slice(0, 10),
          } as any,
          apiToken: controller.apiToken,
          extra: { division: divisionId },
        }).catch((e: any) => [null, e]);
        if (nkdaErr) partialErrors.push(apiErrorTextOf(nkdaErr) || "ปิดสถานะ ไม่เคยมีประวัติแพ้ เดิมไม่สำเร็จ");
        else nkdaRowId = undefined;
      }

      if (profile.nkda) {
        /* ยังไม่เคยถูกบันทึก -> กันสร้าง NKDA ซ้ำทุกครั้งที่กด save (V3 Allergy.ts:807 ใช้ items: []) */
        if (!slice.healthProfile.nkda) {
          const [adrRes, adrErr] = await AdverseReactionList.create({
            data: {
              action: "NO_REACTION",
              items: [],
              note: "ปฏิเสธการแพ้ทุกประเภท (NKDA)",
              type: typeMap["อื่น ๆ"],
              type_name_name: "OTHER",
              patient: patientId,
            } as any,
            apiToken: controller.apiToken,
            extra: { division: divisionId },
          }).catch((e: any) => [null, e]);
          if (adrErr) partialErrors.push(apiErrorTextOf(adrErr) || "บันทึก NKDA ไม่สำเร็จ");
          else nkdaRowId = asNumber((adrRes as any)?.id) ?? nkdaRowId;
        } else {
          /* เดิมเป็น NKDA อยู่แล้ว -> เก็บ id ของแถวเก่าไว้เผื่อถูกถอดติ๊กในครั้งถัดไป */
          nkdaRowId = nkdaRowId ?? asNumber(slice.healthProfile.nkdaRecordId);
        }
      } else if (Array.isArray(profile.allergies)) {
        /* ชื่อสารที่แพ้ต้องไปอยู่ใน AdverseReactionAdversary.other_adversary
           (AdverseReactionSerializerForCreate ทำ name = get_adversaries_summary read_only -> ส่ง name แล้วไม่ถูกเก็บ) */
        const newItems = profile.allergies
          .filter((item) => !item.id && String(item.name || "").trim())
          .map((item) => {
            const typeCode = typeMap[item.type] ?? 4;
            const typeNames = ["DRUG", "FOOD", "SUPPLIES", "OTHER"];
            return {
              pk: null,
              patient: patientId,
              item_action: "NOTE",
              action: "NOTE",
              /* แบบเดียวกับ V3 Allergy.ts:403-425 -> type เป็นชื่อ Choices + typeCreate เป็นเลข */
              type: typeNames[typeCode - 1],
              typeCreate: typeCode,
              adversary_type: adversaryTypeMap[item.type] ?? "OTHER",
              selected_adversary: null,
              adversary_name: "",
              other_adversary: item.name,
              adversary_symptom_type: "ALLERGY",
              probability: null,
              severity: null,
              is_acknowledged: false,
              inactive_start_date: null,
              naranjo: "{}",
              symptoms: [],
              note: item.react && item.react !== "-" ? item.react : "",
              other_symptom: item.react && item.react !== "-" ? item.react : "",
            };
          });

        if (newItems.length > 0) {
          const [adrRes, adrErr] = await MultiAdverseReactionList.create({
            data: { action: "NOTE", patient: patientId, items: newItems } as any,
            apiToken: controller.apiToken,
            extra: { division: divisionId },
          }).catch((e: any) => [null, e]);

          if (adrErr) {
            partialErrors.push(apiErrorTextOf(adrErr) || "บันทึกประวัติแพ้ไม่สำเร็จ");
          } else {
            /* MultiAdverseReactionSerializer.to_representation คืน items เฉพาะแถวที่ผูกกับ
               ingredient/drug group ของ master -> แถว free-text (adversary_type OTHER) จะตอบ items: []
               จึงอ่าน adverse-reaction_list กลับเพื่อเก็บ id ของแถวใหม่ */
            const [refetchRes, refetchErr] = await AdverseReactionList.list({
              params: { patient: patientId },
              apiToken: controller.apiToken,
            }).catch((e: any) => [null, e]);

            if (refetchErr) {
              partialErrors.push(apiErrorTextOf(refetchErr) || "อ่านประวัติแพ้หลังบันทึกไม่สำเร็จ");
            } else {
              const saved = itemsOf(refetchRes).filter(
                (row: any) => row.status !== "CANCELED" && row.status !== "NO_REACTION",
              );
              newItems.forEach((item) => {
                const match = saved.find((row: any) => (row.name || row.adr_short_info) === item.other_adversary);
                const original = profile.allergies.find((a) => !a.id && a.name === item.other_adversary);
                if (original && match && match.id) original.id = asNumber(match.id);
              });
            }
          }
        }
      }

      /* 2b. ลบประวัติแพ้ = AdverseReaction ACTION.CANCEL (STATUS.NOTED -> CANCELED)
         backend ไม่ยอม DELETE และ ActionLog เก็บผู้แก้/เวลาไว้ (issue 86615 ข้อ 6 + ข้อ 9) */
      const previousIds = (slice.healthProfile?.allergies || [])
        .map((item) => asNumber(item.id))
        .filter((id): id is number => typeof id === "number");
      const keptIds = new Set(
        (profile.allergies || []).map((item) => asNumber(item.id)).filter((id): id is number => typeof id === "number"),
      );
      const removedIds = params.removedAllergyIds?.length
        ? params.removedAllergyIds.map((id: any) => asNumber(id)).filter((id): id is number => typeof id === "number")
        : previousIds.filter((id) => !keptIds.has(id));

      for (const adrId of removedIds) {
        const [, cancelErr] = await AdverseReactionDetail.patch({
          pk: adrId,
          data: { action: "CANCEL", note: "ยกเลิกประวัติแพ้จากหน้าคัดกรอง" } as any,
          apiToken: controller.apiToken,
          extra: { division: divisionId },
        }).catch((e: any) => [null, e]);
        if (cancelErr) {
          partialErrors.push(apiErrorTextOf(cancelErr) || `ลบประวัติแพ้ #${adrId} ไม่สำเร็จ`);
          break;
        }
      }

      /* 3. โรคประจำตัว / ยาที่ใช้ประจำ / ประวัติผ่าตัด / สูบบุหรี่ / ดื่มสุรา -> ProblemList (เก็บตาม HN)
         ใช้ problemPlanOf เพื่อ แก้ row เดิม / เพิ่มเฉพาะที่ขาด / ปิด is_active ของแถวที่เลิกใช้ */
      {
        const [existingRes, existingErr] = await ProblemListList.list({
          params: { patient: patientId, is_active: true },
          apiToken: controller.apiToken,
        }).catch((e: any) => [null, e]);

        if (existingErr) {
          partialErrors.push(apiErrorTextOf(existingErr) || "อ่าน ProblemList ไม่สำเร็จ");
        } else {
          const rows: ProblemListRow[] = itemsOf(existingRes).map((row: any) => ({
            id: asNumber(row.id) ?? 0,
            message: String(row.message ?? ""),
            is_active: row.is_active !== false,
            edited: row.edited,
            created: row.created,
            edit_user: row.edit_user,
          }));
          const plan = problemPlanOf(splitProfileProblems(rows).rows, profile);

          for (const update of plan.updates) {
            const [, err] = await ProblemListDetail.patch({
              pk: update.id,
              data: { message: update.message, is_active: true } as any,
              apiToken: controller.apiToken,
              extra: { division: divisionId },
            }).catch((e: any) => [null, e]);
            if (err) partialErrors.push(apiErrorTextOf(err) || "แก้ไขประวัติสุขภาพเดิมไม่สำเร็จ");
          }

          for (const deactivate of plan.deactivates) {
            /* /apis/DPO/problem-list/<pk>/ ไม่อนุญาต DELETE -> ปิดแถวด้วย is_active: false */
            const [, err] = await ProblemListDetail.patch({
              pk: deactivate.id,
              data: { is_active: false } as any,
              apiToken: controller.apiToken,
              extra: { division: divisionId },
            }).catch((e: any) => [null, e]);
            if (err) partialErrors.push(apiErrorTextOf(err) || "ยกเลิกประวัติสุขภาพเดิมไม่สำเร็จ");
          }

          for (const create of plan.creates) {
            const [, err] = await ProblemListList.create({
              data: { patient: patientId, message: create.message, is_active: true } as any,
              apiToken: controller.apiToken,
              extra: { division: divisionId },
            }).catch((e: any) => [null, e]);
            if (err) {
              partialErrors.push(apiErrorTextOf(err) || `เพิ่ม "${create.message}" ไม่สำเร็จ`);
              break;
            }
          }
        }
      }
    }


    // Read server-owned metadata after writes, including deactivations and partial saves.
    let refreshedProblems: ProblemListRow[] | undefined;
    if (patientId) {
      const tuple = await ProblemListList.list({
        params: { patient: patientId, is_active: true }, apiToken: controller.apiToken,
        extra: { division: divisionId },
      }).catch((error: any) => [null, error]);
      const reloadError = apiErrorTextOf(tuple[1]);
      if (!tuple[0] || reloadError) partialErrors.push(reloadError || "อ่านผู้บันทึกประวัติสุขภาพล่าสุดไม่สำเร็จ");
      else refreshedProblems = itemsOf(tuple[0]).map((row: any) => ({
        id: asNumber(row.id) ?? 0, message: String(row.message ?? ""), is_active: row.is_active !== false,
        created: row.created, edited: row.edited, edit_user: row.edit_user, edit_user_name: row.edit_user_name,
      }));
    }

    // 4. แจ้ง refresh encounter เพื่อให้ Patient Header Panel ด้านบนอัปเดตข้อมูลล่าสุดทันที
    controller.handleEvent?.({
      message: "HandleRefreshEncounter",
      params: {},
    });

    const updatedProfile: HealthProfileData = {
      ...profile,
      nkdaRecordId: nkdaRowId,
      recordedAt: new Date().toLocaleDateString("th-TH") + " " + new Date().toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" }),
    };

    writeSlice(
      controller,
      meta,
      { healthProfile: updatedProfile, ...savedStampOf(controller, params),
        ...(refreshedProblems ? { problemRows: refreshedProblems, problemRowsUpdatedAt: new Date().toISOString() } : {}) },
      partialErrors.length > 0 ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS,
      partialErrors.length > 0 ? partialErrors.join(" · ") : null,
    );
    return updatedProfile;
  } catch (err: any) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, errorTextOf(err));
  }
};

/**
 * 4. SAVE_VITAL_SIGN: บันทึกการวัดสัญญาณชีพชุดใหม่
 */
export const SaveVitalSign: Handler = async (controller, params) => {
  const meta: SeqMeta = { sequence: SEQUENCE_NAME, card: CARD_NAME, action: ACTIONS.SAVE_VITAL_SIGN };
  writeSlice(controller, meta, {}, BLC_STATUS.LOADING, null);

  const slice = currentScreenSlice(controller);
  const input = (params.input || params.vitalSign) as NewVitalSignInput;
  const encounterId = asNumber(params.encounterId) ?? slice.encounterId;
  const divisionId = asNumber(controller.data?.division) ?? asNumber(controller.getState().selectedDivision?.id);

  if (!input) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, "ไม่มีข้อมูลสัญญาณชีพที่จะบันทึก");
    return;
  }
  if (!encounterId) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, "ยังไม่มี Encounter สำหรับบันทึกสัญญาณชีพ");
    return;
  }

  try {
    /* POST /apis/PTM/measurement/ ต้องการ { encounter, date(วว/ดด/ปปปป), time(HH:MM), vital_signs:[{vitalsign_type:<id>,result}] }
       - ไม่มี date_recorded/time_recorded/remark ใน Measurement model
       - ถ้าไม่มี vital_signs backend ตอบ 400 "no vital_signs found."
       - backend คำนวณ BMI ให้เองจาก Weight/Height (ห้ามส่ง BMI เอง) */
    const values: Record<string, string> = {
      BP_SYS: input.bpSys,
      BP_DIA: input.bpDia,
      PULSE: input.pulse,
      TEMP: input.temp,
      RR: input.rr,
      SPO2: input.spo2,
      HEIGHT: input.height,
      WEIGHT: input.weight,
      WAIST: input.waist,
      PAIN_SCORE: input.painScore,
    };

    const payload = measurementCreatePayload({
      encounterId,
      dateText: input.customDateTime && input.date ? input.date : todayBeDateText(),
      timeText: input.customDateTime && input.time ? input.time : nowHourMinuteText(),
      remark: input.remark,
      values,
      types: slice.vitalSignTypes,
    });

    if (payload.vital_signs.length === 0) {
      writeSlice(
        controller,
        meta,
        {},
        BLC_STATUS.ERROR,
        "ไม่มีค่าสัญญาณชีพที่ master ของหน่วยงานนี้รองรับ (ชนิดวัดที่ส่งได้ต้องตรงกับช่องที่กรอก)",
      );
      return;
    }

    const [resp, err] = await MeasurementList.create({
      data: payload as any,
      apiToken: controller.apiToken,
      extra: { division: divisionId },
    });

    if (err || !resp || !asNumber(resp.id)) {
      writeSlice(
        controller,
        meta,
        {},
        BLC_STATUS.ERROR,
        apiErrorTextOf(err) || "บันทึกสัญญาณชีพไม่สำเร็จ",
      );
      return;
    }

    const saved = vitalSignMeasurementsFromApi({ items: [resp] })[0];
    const updatedList = [saved, ...slice.vitalSignMeasurements];
    const height = asNumber(saved.values?.["HEIGHT"]);

    writeSlice(
      controller,
      meta,
      {
        vitalSignMeasurements: updatedList,
        latestHeight: height ?? slice.latestHeight,
        vitalSavedAt: [saved.dateRecorded, saved.timeRecorded].filter(Boolean).join(" "),
        ...savedStampOf(controller, params),
      },
      BLC_STATUS.SUCCESS,
      null,
    );
    return saved;
  } catch (err: any) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, errorTextOf(err));
  }
};

/**
 * 5. DELETE_MEASUREMENT: ยกเลิก/ลบรายการวัดสัญญาณชีพ
 */
export const DeleteMeasurement: Handler = async (controller, params) => {
  const meta: SeqMeta = { sequence: SEQUENCE_NAME, card: CARD_NAME, action: ACTIONS.DELETE_MEASUREMENT };
  writeSlice(controller, meta, {}, BLC_STATUS.LOADING, null);

  const slice = currentScreenSlice(controller);
  const measureId = asNumber(params.measureId) ?? asNumber(params.measurementId);
  const username = String(params.username || controller.getState()?.userProfile?.username || "").trim();
  const password = String(params.password || "").trim();

  if (!measureId) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, "ระบุรหัสการวัดไม่ถูกต้อง");
    return;
  }

  /* MeasurementCancelSerializer.validate() เรียก AuthenticationForm -> backend ต้องได้ username+password
     (V3 CardVitalSign.tsx:243 ส่ง {username, password, active:false, measurement_id}) */
  if (!username || !password) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, "ยกเลิกการวัดต้องยืนยันผู้ใช้/รหัสผ่านก่อน");
    return;
  }

  const audit: VitalAuditEntry = {
    action: "CANCEL",
    measurementId: measureId,
    label: String(params.label || `#${measureId}`).trim(),
    user: username,
    at: nowBeDateTimeText(),
  };

  try {
    const [, err] = await MeasurementCancelView.patch({
      pk: measureId,
      data: { username, password, active: false, measurement_id: measureId } as any,
      apiToken: controller.apiToken,
      extra: { division: asNumber(controller.data?.division) },
    });

    if (err) {
      writeSlice(controller, meta, {}, BLC_STATUS.ERROR, apiErrorTextOf(err) || "ยกเลิกการวัดไม่สำเร็จ");
      return;
    }

    writeSlice(
      controller,
      meta,
      {
        /* issue 86618 ข้อ 6: เก็บประวัติการลบ (ผู้ลบ + เวลา) ไว้ในหน้า
           backend เก็บแถวเดิม (active=false) แต่ list คืนเฉพาะ active */
        vitalSignMeasurements: slice.vitalSignMeasurements.filter((m) => m.id !== measureId),
        vitalAudit: [audit, ...(slice.vitalAudit || [])].slice(0, 20),
      },
      BLC_STATUS.SUCCESS,
      null,
    );
  } catch (err: any) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, errorTextOf(err));
  }
};

/**
 * 6. SAVE_BODY_COMPOSITION: บันทึกผลตรวจองค์ประกอบร่างกาย
 */
export const SaveBodyComposition: Handler = async (controller, params) => {
  const meta: SeqMeta = { sequence: SEQUENCE_NAME, card: CARD_NAME, action: ACTIONS.SAVE_BODY_COMPOSITION };
  writeSlice(controller, meta, {}, BLC_STATUS.LOADING, null);

  const screen = currentScreenSlice(controller);
  const state = controller.getState();
  const encounterId = asNumber(params.encounterId) ?? screen.encounterId ?? asNumber(state.selectedEncounter?.id);
  const divisionId = asNumber(controller.data?.division) ?? asNumber(state.selectedDivision?.id);
  const record = (params.record || params.bodyComposition || {}) as Partial<BodyCompositionRecord>;
  const file = params.file as any;

  /* gate ตาม #86619 ข้อ 5 (ฝั่ง backend ก็ validate ซ้ำ - ห้ามส่งค่าที่ไม่พร้อม) */
  const gateError = bodyCompositionSaveGateErrorOf(record);
  if (gateError) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, gateError);
    return null;
  }
  if (!encounterId) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, "ไม่พบ Encounter ของผู้ป่วย ไม่สามารถบันทึกผลองค์ประกอบร่างกายได้");
    return null;
  }

/**
 * FormData ต้องเป็น string ทุก field และ array ต้องถูก encode เป็น JSON string
 * DRF JSONField (PTM.BodyComposition.ocr_ambiguities) ตอบ 400 "ค่าจะต้องเป็น JSON ที่ถูกต้อง"
 * ถ้า send `["a","b"]` แบบธรรมดา ซึ่ง browser จะ join ให้เป็น "a,b"
 */
const appendFormValue = (form: FormData, key: string, value: any): void => {
  if (value === undefined || value === null) {
    form.append(key, "");
    return;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    form.append(key, String(value));
    return;
  }
  form.append(key, JSON.stringify(value));
};

  const payload = bodyCompositionCreatePayload(record, encounterId);
  /* เก็บไฟล์ต้นฉบับไว้ดูย้อนหลัง (issue 86619 ข้อ 7) -> multipart ที่มี source_file */
  const data: Record<string, any> | FormData = file
    ? (() => {
        const form = new FormData();
        Object.entries(payload).forEach(([key, value]) => appendFormValue(form, key, value));
        form.append("source_file", file, file.name || "body_composition");
        return form;
      })()
    : payload;

  const [created, createErr] = await BodyCompositionList.create({
    data: data as any,
    apiToken: controller.apiToken,
    extra: { division: divisionId },
  });

  if (createErr || !created) {
    writeSlice(
      controller,
      meta,
      {},
      BLC_STATUS.ERROR,
      friendlyBodyCompositionErrorText(errorTextOf(createErr)),
    );
    return null;
  }

  /* แถวที่สร้างจริงต้อง active=True ไม่งั้น list (ซึ่งกรอง active=True) จะไม่แสดง
     ผู้ใช้จะเห็น "บันทึกสำเร็จ" แต่พอเปลี่ยน tab แล้วข้อมูลหาย (เคยเกิดจริงเพราะ DRF อ่าน
     BooleanField จาก multipart ไม่ได้ -> ได้แถว active=false) - ห้ามรายงาน SUCCESS */
  if (created && (created as any).active === false) {
    writeSlice(
      controller,
      meta,
      {},
      BLC_STATUS.ERROR,
      "บันทึกไม่สมบูรณ์: backend เก็บแถวเป็นสถานะปิดใช้งาน (active=false) ข้อมูลจะไม่แสดงในประวัติ กรุณาแจ้งทีม backend",
    );
    return null;
  }

  /* id ของแถวจริงอาจต่างจาก response (เหมือน #86747) และคอลัมน์ประวัติต้องอ่านกลับใหม่ */
  const recheck = await BodyCompositionList.list({
    params: { encounter: encounterId, use_patient: true, invert_order: true },
    apiToken: controller.apiToken,
    extra: { division: divisionId },
  }).catch(() => [null]);
  const latestList = itemsOf(recheck?.[0])
    .map((row: any) => bodyCompositionFromApi(row))
    .filter((item): item is BodyCompositionRecord => !!item && item.id !== undefined);

  const savedRecord = bodyCompositionFromApi(created) || {
    ...record,
    dateRecorded: String(payload.date),
    timeRecorded: String(payload.time),
  };
  const stamp = savedStampOf(controller, params);

  writeSlice(
    controller,
    meta,
    {
      ...screen,
      bodyCompositionList: latestList.length > 0 ? latestList : [savedRecord as BodyCompositionRecord],
      bodyCompositionOcr: null,
      /* ข้อความต้องตรงกับค่าที่เก็บจริง (ใบที่ไม่มีเวลา = เวลาปัจจุบัน ตาม resolveBcTime) */
      bodyCompositionSavedMessage: bodyCompositionSavedMessageOf(
        String(payload.date),
        String(payload.time),
      ),
      savedAt: stamp.savedAt,
      savedBy: stamp.savedBy,
      encounterId,
      patientId: screen.patientId ?? asNumber(state.selectedEncounter?.patient_id),
    },
    BLC_STATUS.SUCCESS,
    null,
  );
  return created;
};

/**
 * 6.1 OCR_BODY_COMPOSITION: อ่านค่าจากไฟล์ผลตรวจ (PDF/รูปภาพ) หรือจากข้อความที่ผู้ใช้แก้
 *     POST /apis/PTM/body-composition-ocr/  (multipart {file} หรือ json {text})
 */
export const OcrBodyComposition: Handler = async (controller, params) => {
  const meta: SeqMeta = { sequence: SEQUENCE_NAME, card: CARD_NAME, action: ACTIONS.OCR_BODY_COMPOSITION };
  writeSlice(controller, meta, {}, BLC_STATUS.LOADING, null);

  const screen = currentScreenSlice(controller);
  const state = controller.getState();
  const encounterId = asNumber(params.encounterId) ?? screen.encounterId ?? asNumber(state.selectedEncounter?.id);
  const divisionId = asNumber(controller.data?.division) ?? asNumber(state.selectedDivision?.id);
  const file = params.file as any;
  const text = typeof params.text === "string" ? params.text.trim() : "";

  if (!file && !text) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, "ต้องแนบไฟล์ผลตรวจ (PDF หรือรูปภาพ) หรือส่งข้อความเพื่อแปลงค่าใหม่");
    return null;
  }

  const data: Record<string, any> | FormData = file
    ? (() => {
        const form = new FormData();
        form.append("file", file, file.name || "body_composition");
        if (encounterId) form.append("encounter", String(encounterId));
        if (params.pdfPassword) form.append("pdf_password", String(params.pdfPassword));
        return form;
      })()
    : { text };

  const [res, err] = await BodyCompositionOCRView.post({
    data: data as any,
    apiToken: controller.apiToken,
    extra: { division: divisionId },
  });

  const ocr = bodyCompositionOcrFromApi(res);
  if (err || !ocr) {
    writeSlice(
      controller,
      meta,
      {},
      BLC_STATUS.ERROR,
      friendlyBodyCompositionErrorText(errorTextOf(err)),
    );
    return null;
  }

  writeSlice(
    controller,
    meta,
    {
      ...screen,
      bodyCompositionOcr: ocr as BodyCompositionOcrResult,
      encounterId,
      patientId: screen.patientId ?? asNumber(state.selectedEncounter?.patient_id),
    },
    BLC_STATUS.SUCCESS,
    null,
  );
  return ocr;
};

export const SendToQueue: Handler = async (controller, params) => {
  const meta: SeqMeta = { sequence: SEQUENCE_NAME, card: CARD_NAME, action: ACTIONS.SEND_TO_QUEUE };
  writeSlice(controller, meta, {}, BLC_STATUS.LOADING, null);

  const slice = currentScreenSlice(controller);
  const encounterId = asNumber(params.encounterId) ?? slice.encounterId;

  if (!encounterId) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, "ยังไม่มี Encounter จะส่งผู้ป่วยเข้าคิวไม่ได้");
    return;
  }

  /* ข้อ 5: "กดส่งแล้ว ต้องบันทึกข้อมูลทั้งหมดก่อน" -> ใช้ handler เดียวกันกับปุ่มอื่น
     (triage/healthProfile ที่การ์ดส่งมาใน params เท่านั้น จึงไม่ เดา ค่าใน slice) */
  if (params.triage) {
    await SaveTriage(controller, { ...params, action: ACTIONS.SAVE_TRIAGE });
  }
  if (params.healthProfile) {
    await SaveProfile(controller, { ...params, action: ACTIONS.SAVE_PROFILE });
  }

  const status = asNumber(params.encounterStatus ?? slice.encounterStatus);

  /* core/models.py TRANSITION:
     - OPD_SCREEN ได้จาก WAIT_TRIAGE(1) / ARRIVING(2) / ARRIVED(3) เท่านั้น
     - OPD_QUEUE_EXAM ได้จาก SCREENED(4) ขึ้นไป (core/models.py:5347)
     เดิมยิง OPD_QUEUE_EXAM ครั้งเดียว -> encounter ที่ยัง ARRIVING ตอบ ACTION_NOT_ALLOWED ทุกครั้ง */
  if (status === undefined || ENCOUNTER_STATUS_NEEDS_SCREEN_ACTION.includes(status)) {
    const [, screenErr] = await EncounterActionOnly.patch({
      pk: encounterId,
      data: { action: "OPD_SCREEN" } as any,
      apiToken: controller.apiToken,
      extra: { division: asNumber(controller.data?.division) },
    });
    if (screenErr) {
      writeSlice(controller, meta, {}, BLC_STATUS.ERROR, apiErrorTextOf(screenErr) || "บันทึกการคัดกรอง (OPD_SCREEN) ไม่สำเร็จ");
      return;
    }
  }

  try {
    const [, err] = await EncounterActionOnly.patch({
      pk: encounterId,
      data: { action: "OPD_QUEUE_EXAM" } as any,
      apiToken: controller.apiToken,
      extra: { division: asNumber(controller.data?.division) },
    });

    if (err) {
      /* EncounterActionOnlySerializer ต้องมี แพทย์ ใน Encounter ก่อนส่งเข้าคิวรอตรวจ */
      writeSlice(controller, meta, {}, BLC_STATUS.ERROR, apiErrorTextOf(err) || "ส่งผู้ป่วยเข้าคิวไม่สำเร็จ");
      return;
    }

    controller.handleEvent?.({
      message: "HandleRefreshEncounter",
      params: {},
    });

    writeSlice(
      controller,
      meta,
      {
        sentToQueue: true,
        encounterStatus: 5,
        encounterStatusName: "รอพบแพทย์",
        sentEncounterStatus: "รอพบแพทย์",
      },
      BLC_STATUS.SUCCESS,
      null,
    );
  } catch (err: any) {
    writeSlice(controller, meta, {}, BLC_STATUS.ERROR, errorTextOf(err));
  }
};

export const SequenceIndex: Record<string, Handler> = {
  [ACTIONS.LOAD]: LoadScreen,
  [ACTIONS.SAVE_TRIAGE]: SaveTriage,
  [ACTIONS.SAVE_PROFILE]: SaveProfile,
  [ACTIONS.SAVE_VITAL_SIGN]: SaveVitalSign,
  [ACTIONS.DELETE_MEASUREMENT]: DeleteMeasurement,
  [ACTIONS.SAVE_BODY_COMPOSITION]: SaveBodyComposition,
  [ACTIONS.OCR_BODY_COMPOSITION]: OcrBodyComposition,
  [ACTIONS.SEND_TO_QUEUE]: SendToQueue,
};

export const Action = async (
  controller: Controller,
  params: ScreenRunParams,
): Promise<void> => {
  const handler = SequenceIndex[params.action] || SequenceIndex[ACTIONS.LOAD];
  if (handler) {
    await handler(controller, params);
  }
};

export const Start = async (
  controller: Controller,
  params: ScreenRunParams,
): Promise<void> => {
  const current = controller.getState()[SCREEN_SLICE_KEY];
  if (!current || params.restart) {
    controller.setState({
      [SCREEN_SLICE_KEY]: {
        ...INITIAL_SCREEN_SLICE,
        patientHn: params.patientHn ?? "",
        encounterId: asNumber(params.encounterId),
        patientId: asNumber(params.patientId),
        doctorId: asNumber(params.doctorId),
        sequenceIndex: "Action",
      },
    });
  }
  await Action(controller, params);
};

export const ScreenV4 = {
  [CARD_NAME]: SequenceIndex,
};

export default ScreenV4;

